import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { PageHeader } from '../../components/PageHeader';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { SkeletonTable } from '../../components/Skeleton';
import {
  useChecklistTemplates,
  useCreateChecklistTemplate,
  useDeleteChecklistTemplate,
} from './checklistQueries';

/**
 * Checklistenvorlagen -- F1-19, K-14.
 *
 * **Die Punkte stehen als Liste, eine Zeile je Punkt.** Eine Vorlage, die aus
 * einem Absatz besteht, laesst sich nicht abhaken -- und genau das ist der
 * Zweck einer Checkliste.
 *
 * **Eine Aenderung schreibt keine laufende Abarbeitung um.** Beim Anlegen am
 * Ticket werden die Punkte kopiert; sonst verschwaende ein bereits gesetzter
 * Haken, weil jemand die Vorlage angepasst hat. Der Hinweis steht in der
 * Maske, weil sonst niemand damit rechnet.
 */
export function ChecklistTemplatePage() {
  const { t } = useTranslation();
  const templates = useChecklistTemplates();
  const create = useCreateChecklistTemplate();
  const remove = useDeleteChecklistTemplate();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [items, setItems] = useState('');

  const parsedItems = items
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '');

  return (
    <>
      <PageHeader
        title={t('checklistTemplates.title')}
        subtitle={t('checklistTemplates.subtitle')}
      />

      <section className="card card--flush">
        {templates.isPending ? <SkeletonTable columns={3} /> : null}
        {templates.isError ? <ErrorNotice error={templates.error} /> : null}

        {templates.data?.length === 0 ? (
          <EmptyState
            icon="checklists"
            title={t('checklistTemplates.empty')}
            hint={t('checklistTemplates.emptyHint')}
          />
        ) : null}

        {templates.data && templates.data.length > 0 ? (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('masterdata.name')}</th>
                  <th scope="col">{t('checklistTemplates.items')}</th>
                  <th scope="col">{t('tickets.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {templates.data.map((template) => (
                  <tr key={template.id}>
                    <td>
                      {template.name}{' '}
                      {template.is_active ? null : (
                        <Badge tone="neutral">{t('masterdata.inactive')}</Badge>
                      )}
                      {template.description ? (
                        <div className="table__muted">{template.description}</div>
                      ) : null}
                    </td>
                    <td className="table__muted">{template.items.join(' · ')}</td>
                    <td>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          remove.mutate(template.id);
                        }}
                      >
                        {t('masterdata.delete')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      {remove.isError ? <ErrorNotice error={remove.error} /> : null}

      <section className="card">
        <h2>{t('checklistTemplates.new')}</h2>
        <p className="table__muted">{t('checklistTemplates.copyHint')}</p>

        <div className="field">
          <label className="field__label" htmlFor="template-name">
            {t('masterdata.name')}
          </label>
          <input
            id="template-name"
            className="field__control"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="template-description">
            {t('checklistTemplates.description')}
          </label>
          <input
            id="template-description"
            className="field__control"
            value={description}
            onChange={(event) => {
              setDescription(event.target.value);
            }}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="template-items">
            {t('checklistTemplates.items')}
          </label>
          <textarea
            id="template-items"
            className="field__control"
            rows={6}
            value={items}
            onChange={(event) => {
              setItems(event.target.value);
            }}
          />
          <span className="field__hint">{t('checklistTemplates.itemsHint')}</span>
        </div>

        {create.isError ? <ErrorNotice error={create.error} /> : null}

        <Button
          variant="primary"
          disabled={!name.trim() || parsedItems.length === 0}
          busy={create.isPending}
          onClick={() => {
            create.mutate(
              { name: name.trim(), description, items: parsedItems },
              {
                onSuccess: () => {
                  setName('');
                  setDescription('');
                  setItems('');
                },
              },
            );
          }}
        >
          {t('masterdata.add')}
        </Button>
      </section>
    </>
  );
}
